import React, { useState } from 'react';
import { VERDICTS } from '../copy';
import { compactMoney, fullMoney } from '../format';

// "Am I OK?" hero (P1-07 v2): the key figure is the gap (or surplus), the
// verdict pill, the takeaway, and goal vs projected bars with a table view
// for screen readers and anyone who prefers numbers.
export default function WealthPictureBody({ artifact, data }) {
  const [asTable, setAsTable] = useState(false);
  const verdict = VERDICTS[data.verdict] || VERDICTS.close;
  const goal = data.goal || 0;
  const savings = data.projected_savings || 0;
  const proceeds = data.after_tax_proceeds || 0;
  const projected = data.projected_total || savings + proceeds;
  const gap = data.gap || 0;
  const scale = Math.max(goal, projected, 1);
  const w = (v) => `${Math.max(0, Math.min(100, (v / scale) * 100))}%`;
  const surplus = gap < 0;

  const rows = [
    ['Your goal', fullMoney(goal)],
    [`Savings at exit (from ${compactMoney(data.current_assets)})`, fullMoney(savings)],
    ['Business value', fullMoney(data.business_value)],
    ['After-tax sale proceeds', fullMoney(proceeds)],
    ['Projected at sale', fullMoney(projected)],
    [surplus ? 'Surplus' : 'Gap', fullMoney(Math.abs(gap))],
    ['Coverage', `${data.coverage_pct}%`],
  ];

  return (
    <div className="flex flex-col gap-[22px]">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="flex flex-col gap-1">
          <span className="font-display font-light text-[clamp(40px,6vw,56px)] leading-none tracking-[-0.02em] tabular-nums text-foreground">
            {compactMoney(Math.abs(gap))}
          </span>
          <span className="text-[14px] text-muted-foreground">
            {surplus ? `to spare beyond your ${compactMoney(goal)} goal` : `short of your ${compactMoney(goal)} goal`}
          </span>
        </div>
        <span
          className="inline-flex items-center gap-2 py-1.5 px-3 rounded-full text-[13px] font-medium"
          style={{ background: verdict.tint, color: verdict.ink }}
        >
          <span className="w-[7px] h-[7px] rounded-full" style={{ background: verdict.color }} />
          {verdict.label}
        </span>
      </div>
      {artifact.summary && (
        <p className="-mt-2 mx-0 mb-0 text-[15px] leading-[1.6] text-foreground/[0.88]">{artifact.summary}</p>
      )}

      {asTable ? (
        <table className="w-full border-collapse text-[14px] tabular-nums">
          <caption className="sr-only">Wealth picture as a table</caption>
          <tbody>
            {rows.map(([k, v]) => (
              <tr key={k} className="border-t border-border">
                <th scope="row" className="text-left font-normal py-2.5 px-0 text-muted-foreground">{k}</th>
                <td className="text-right py-2.5 px-0">{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div
          role="img"
          aria-label={`Goal ${fullMoney(goal)}; projected ${fullMoney(projected)}: savings ${fullMoney(savings)} plus after-tax proceeds ${fullMoney(proceeds)}; ${surplus ? 'surplus' : 'gap'} ${fullMoney(Math.abs(gap))}.`}
          className="flex flex-col gap-2"
        >
          <div className="flex justify-between text-[13px]">
            <span>
              Your goal
              {data.annual_income_needed ? <span className="text-muted-foreground"> · {compactMoney(data.annual_income_needed)} a year</span> : null}
            </span>
            <span className="tabular-nums">{compactMoney(goal)}</span>
          </div>
          <div className="h-3.5 rounded-full bg-foreground/[0.06] border border-foreground/35" style={{ width: w(goal) }} />
          <div className="flex h-3.5 gap-0.5 rounded-full overflow-hidden mt-2.5">
            <div className="bg-muted-foreground/70" style={{ width: w(savings) }} />
            <div className="bg-succeedo-orange" style={{ width: w(proceeds) }} />
            {!surplus && gap > 0 && (
              <div
                className="box-border border border-dashed"
                style={{
                  width: w(gap),
                  borderColor: verdict.color,
                  background: `repeating-linear-gradient(135deg, ${verdict.tint} 0 3px, transparent 3px 7px)`,
                }}
              />
            )}
          </div>
          <div className="flex justify-between gap-3 text-[13px]">
            <span>Projected at sale, before any actions</span>
            <span className="tabular-nums">{compactMoney(projected)}</span>
          </div>
          <div className="flex flex-wrap gap-y-2 gap-x-5 mt-1.5 text-[12px] text-muted-foreground tabular-nums">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-[3px] bg-muted-foreground/70" />
              Savings {compactMoney(data.current_assets)} → {compactMoney(savings)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-[3px] bg-succeedo-orange" />
              After-tax sale proceeds {compactMoney(proceeds)}
            </span>
            {!surplus && gap > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-[3px] box-border border border-dashed" style={{ borderColor: verdict.color }} />
                Gap {compactMoney(gap)}
              </span>
            )}
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={() => setAsTable(!asTable)}
        className="self-start bg-transparent border-0 p-0 text-[13px] text-muted-foreground underline underline-offset-[3px] cursor-pointer"
      >
        {asTable ? 'Show as a chart' : 'Show as a table'}
      </button>
    </div>
  );
}
